import React, { Suspense, useRef, createElement as h } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Sparkles, Environment, MeshDistortMaterial } from "@react-three/drei";

/*
 * NOTE: All three.js primitives (mesh, sphereGeometry, meshStandardMaterial, etc.)
 * are intentionally created via React.createElement (aliased as `h`) instead of JSX.
 * The visual-edits babel plugin injects dev-only attributes like "x-line-number" onto
 * JSX intrinsics, which collide with R3F's nested-property setter and crashes the app.
 * Using createElement bypasses the plugin since it only rewrites JSX syntax.
 */

function GoldRing({ position, scale = 1, speed = 1, rotation = [0, 0, 0] }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.x = rotation[0] + state.clock.elapsedTime * 0.15 * speed;
    ref.current.rotation.y = rotation[1] + state.clock.elapsedTime * 0.2 * speed;
  });
  return h(
    Float,
    { speed: 1.5, rotationIntensity: 0.6, floatIntensity: 1.2 },
    h(
      "mesh",
      { ref, position, scale, rotation },
      h("torusGeometry", { args: [1, 0.06, 64, 128] }),
      h("meshStandardMaterial", {
        color: "#D4AF37",
        metalness: 1,
        roughness: 0.15,
        emissive: "#B8941F",
        emissiveIntensity: 0.25,
      })
    )
  );
}

function GoldOrb({ position, scale = 0.4, color = "#E8C988" }) {
  return h(
    Float,
    { speed: 2, rotationIntensity: 1, floatIntensity: 2 },
    h(
      "mesh",
      { position, scale },
      h("sphereGeometry", { args: [1, 64, 64] }),
      h(MeshDistortMaterial, {
        color,
        distort: 0.25,
        speed: 1.5,
        metalness: 0.9,
        roughness: 0.2,
        emissive: color,
        emissiveIntensity: 0.3,
      })
    )
  );
}

function HangingLantern({ position }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.6) * 0.08;
  });
  return h(
    "group",
    { ref, position },
    h(
      "mesh",
      { position: [0, 1.2, 0] },
      h("cylinderGeometry", { args: [0.005, 0.005, 2.5, 8] }),
      h("meshBasicMaterial", { color: "#D4AF37", opacity: 0.4, transparent: true })
    ),
    h(
      "mesh",
      { position: [0, -0.2, 0] },
      h("sphereGeometry", { args: [0.18, 32, 32] }),
      h("meshStandardMaterial", {
        color: "#F3E5AB",
        emissive: "#E8C988",
        emissiveIntensity: 1.2,
        transparent: true,
        opacity: 0.85,
      })
    ),
    h("pointLight", {
      position: [0, -0.2, 0],
      color: "#F3E5AB",
      intensity: 1.5,
      distance: 3,
    })
  );
}

function Scene() {
  return h(
    React.Fragment,
    null,
    h("ambientLight", { intensity: 0.25 }),
    h("directionalLight", { position: [5, 5, 5], intensity: 0.6, color: "#F3E5AB" }),
    h("pointLight", { position: [-4, -2, 2], color: "#C9302E", intensity: 0.7 }),
    h("pointLight", { position: [4, 3, -2], color: "#D4AF37", intensity: 1.2 }),
    h("pointLight", { position: [0, -3, 1], color: "#E08A1F", intensity: 0.5 }),

    h(GoldRing, { position: [-2.6, 0.6, -1], scale: 1.1, speed: 0.8, rotation: [0.5, 0.2, 0] }),
    h(GoldRing, { position: [2.4, -0.3, -2], scale: 0.85, speed: 1.2, rotation: [0.2, 0.4, 0.3] }),
    h(GoldRing, { position: [0.4, 1.4, -3], scale: 0.55, speed: 0.6, rotation: [0.8, 0, 0.4] }),

    h(GoldOrb, { position: [-1.6, -1, 0], scale: 0.18, color: "#E8C988" }),
    h(GoldOrb, { position: [1.9, 1, -1], scale: 0.12, color: "#F3E5AB" }),
    h(GoldOrb, { position: [-2.2, 1.6, -2], scale: 0.09, color: "#D4AF37" }),
    h(GoldOrb, { position: [2.7, -1.2, -1.5], scale: 0.14, color: "#C9302E" }),
    h(GoldOrb, { position: [-2.9, -0.4, -2.5], scale: 0.1, color: "#E08A1F" }),

    h(HangingLantern, { position: [-3.2, 1.2, -1.5] }),
    h(HangingLantern, { position: [3.0, 1.5, -2.5] }),
    h(HangingLantern, { position: [0.8, 2, -3] }),

    h(Sparkles, { count: 120, size: 3, color: "#F3E5AB", scale: [10, 6, 6], speed: 0.4, opacity: 0.7 }),
    h(Sparkles, { count: 80, size: 2, color: "#D4AF37", scale: [8, 5, 4], speed: 0.3, opacity: 0.5 }),

    h(Environment, { preset: "night" }),
    h("fog", { attach: "fog", args: ["#050B14", 6, 14] })
  );
}

export default function HeroScene() {
  return (
    <Canvas
      camera={{ position: [0, 0, 5], fov: 50 }}
      dpr={[1, 1.8]}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      <Suspense fallback={null}>
        <Scene />
      </Suspense>
    </Canvas>
  );
}
